import type {
  SessionSyncFields,
  SessionSyncPayload,
} from "@cold-case/shared/session-sync";

import { api } from "../net.js";

/** Backup poll while socket is healthy (movement/journal use sockets). */
export const POLL_NORMAL_MS = 12_000;
export const POLL_ACCUSATION_MS = 8000;
export const POLL_RECONNECT_MS = 3000;
export const POLL_BACKGROUND_MS = 20_000;
export const POLL_OFFLINE_MS = 4000;

export type SessionPollMode =
  | "normal"
  | "accusation"
  | "reconnecting"
  | "background"
  | "offline";

export function sessionPollIntervalForMode(mode: SessionPollMode): number {
  switch (mode) {
    case "accusation":
      return POLL_ACCUSATION_MS;
    case "reconnecting":
      return POLL_RECONNECT_MS;
    case "background":
      return POLL_BACKGROUND_MS;
    case "offline":
      return POLL_OFFLINE_MS;
    default:
      return POLL_NORMAL_MS;
  }
}

/** Full sync when reconnecting or socket down; minimal when connected. */
export function sessionSyncFieldsForPollMode(mode: SessionPollMode): SessionSyncFields {
  if (mode === "offline" || mode === "background") {
    return "full";
  }
  return "minimal";
}

export function sessionSyncQuery(fields: SessionSyncFields): string {
  return fields === "minimal" ? "?fields=minimal" : "";
}

export function sessionSyncPath(sessionId: string, fields: SessionSyncFields): string {
  return `/api/sessions/${encodeURIComponent(sessionId)}/sync${sessionSyncQuery(fields)}`;
}

export type SessionPollCallbacks = {
  onSync: (data: SessionSyncPayload) => void | Promise<void>;
  onExpired: () => void;
  isCancelled: () => boolean;
  /** Defaults to `sessionSyncFieldsForPollMode` for the active poll mode. */
  getSyncFields?: () => SessionSyncFields;
};

export type SessionPollController = {
  setMode: (mode: SessionPollMode) => void;
  stop: () => void;
  tickNow: () => void;
  getMode: () => SessionPollMode;
};

export function createSessionPollController(
  getSessionId: () => string | null,
  callbacks: SessionPollCallbacks
): SessionPollController {
  let timer: ReturnType<typeof setInterval> | null = null;
  let mode: SessionPollMode = "normal";
  let inFlight = false;

  const resolveSyncFields = (): SessionSyncFields =>
    callbacks.getSyncFields?.() ?? sessionSyncFieldsForPollMode(mode);

  const run = async (): Promise<void> => {
    const sessionId = getSessionId();
    if (!sessionId || callbacks.isCancelled()) return;
    if (inFlight) return;
    inFlight = true;
    try {
      const fields = resolveSyncFields();
      const s = await api<SessionSyncPayload>(sessionSyncPath(sessionId, fields));
      if (callbacks.isCancelled()) return;
      if (!s.ok) {
        const err = (s.data as { error?: string })?.error;
        if (
          s.status === 404 ||
          s.status === 403 ||
          err === "not_found" ||
          err === "forbidden"
        ) {
          callbacks.onExpired();
        }
        return;
      }
      await Promise.resolve(callbacks.onSync(s.data as SessionSyncPayload));
    } finally {
      inFlight = false;
    }
  };

  const restart = (): void => {
    if (timer) clearInterval(timer);
    timer = null;
    if (callbacks.isCancelled()) return;
    if (!getSessionId()) return;
    void run();
    timer = setInterval(() => void run(), sessionPollIntervalForMode(mode));
  };

  return {
    setMode(next: SessionPollMode) {
      if (mode === next && timer) return;
      mode = next;
      restart();
    },
    stop() {
      if (timer) clearInterval(timer);
      timer = null;
    },
    tickNow() {
      void run();
    },
    getMode() {
      return mode;
    },
  };
}

export async function fetchSessionSync(
  sessionId: string,
  fields: SessionSyncFields = "full"
): Promise<
  | { ok: true; data: SessionSyncPayload }
  | { ok: false; status: number; error?: string }
> {
  const s = await api<SessionSyncPayload>(sessionSyncPath(sessionId, fields));
  if (!s.ok) {
    return {
      ok: false,
      status: s.status,
      error: (s.data as { error?: string })?.error,
    };
  }
  return { ok: true, data: s.data as SessionSyncPayload };
}
